import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useModals from '../useModals';
import useConfirmModal from '../useConfirmModal';
import {
  openAddProductModal,
  closeAddProductModal,
  clearProductToEdit,
  openAddServiceModal,
  closeAddServiceModal,
  clearServiceToEdit,
  openPriceAdjustmentModal,
  closePriceAdjustmentModal,
} from '../../store/slices/purchases/debitNoteSlice';
import { useBasePurchase } from './useBasePurchase';
import DebitNoteService from '../../services/purchases/debitNoteService';
import { updateDebitNoteTotals } from '../../store/slices/purchases/basePurchaseSlice';

export const useDebitNote = () => {
  const dispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const confirm = useConfirmModal();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

  const { addProduct, deleteProduct } = useBasePurchase();
  const {
    productToEdit,
    showAddProductModal,
    serviceToEdit,
    showAddServiceModal,
    showPriceAdjustmentModal
  } = useSelector(
    (state: RootState) => state.debitNote
  );

  const handlerAddProduct = async (product: any) => {
    console.log(product);

    try {
      showLoadingIndicator();
      const response = await DebitNoteService.addProduct(product);
      console.log(response);

      if (response.statusCode === 201) {
        const { title, message, type, data } = response;
        const { products, totals } = data;

        addProduct(products); // ✅ Agrega al estado
        dispatch(updateDebitNoteTotals(totals)); // ✅ Actualiza totales

        const item = Array.isArray(products) ? products[0] : products;
        // 🧠 Cerrar el modal correspondiente según el tipo
        if (item.type === 'NEW_PRODUCT') {
          dispatch(closeAddProductModal());
          dispatch(clearProductToEdit());
        } else if (item.type === 'SERVICE') {
          dispatch(closeAddServiceModal());
          dispatch(clearServiceToEdit());
        } else if (item.type === 'PRICE_INCREASE') {
          dispatch(closePriceAdjustmentModal());
          dispatch(clearProductToEdit());
        }

        openModal(type, { title, message });
      } else {
        showAlert('error', 'Error', 'No se pudo agregar el producto o servicio.');
      }
    } catch (error: any) {
      const response = error?.response?.data;
      if (response?.statusCode === 404 || response?.statusCode === 400) {
        openModal(response.type || 'error', {
          title: response.title || 'Error',
          message: response.message || 'Ocurrió un error al agregar.',
        });
      } else {
        showAlert('error', 'Error', 'Ocurrió un error inesperado.');
      }
      console.error('❌ Error al agregar producto (nota débito):', error);
    } finally {
      hideLoadingIndicator();
    }
  };


  const handlerDeleteProduct = async (id: number) => {
    confirm({
      title: '¿Eliminar producto?',
      message: '¿Estás seguro que deseas eliminar este producto?',
      icon: 'warning',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmArgs: id,
      onConfirm: async (id) => {
        try {
          showLoadingIndicator();
          const response = await DebitNoteService.deleteProduct(id);

          if (response.statusCode === 200) {
            const { title, message, type, data } = response;
            const { totals } = data;

            deleteProduct(id); // ✅ Quita del estado
            dispatch(updateDebitNoteTotals(totals)); // ✅ Actualiza los totales

            showAlert(type, title, message);
          } else {
            showAlert('error', 'Error', 'No se pudo eliminar el producto.');
          }
        } catch (error) {
          console.error('❌ Error al eliminar producto:', error);
          showAlert('error', 'Error', 'Ocurrió un error inesperado.');
        } finally {
          hideLoadingIndicator();
        }
      },
    });
  };


  return {
    productToEdit,
    showAddProductModal,
    serviceToEdit,
    showAddServiceModal,
    showPriceAdjustmentModal,

    openAddProductModal: () => dispatch(openAddProductModal()),
    closeAddProductModal: () => dispatch(closeAddProductModal()),

    openAddServiceModal: () => dispatch(openAddServiceModal()),
    closeAddServiceModal: () => dispatch(closeAddServiceModal()),

    openPriceAdjustmentModal: () => dispatch(openPriceAdjustmentModal()),
    closePriceAdjustmentModal: () => dispatch(closePriceAdjustmentModal()),

    clearProductToEdit: () => dispatch(clearProductToEdit()),
    clearServiceToEdit: () => dispatch(clearServiceToEdit()),

    handleCancelProduct: () => {
      dispatch(closeAddProductModal());
      dispatch(clearProductToEdit());
    },

    handleCancelService: () => {
      dispatch(closeAddServiceModal());
      dispatch(clearServiceToEdit());
    },

    handleCancelPriceAdjustment: () => {
      dispatch(closePriceAdjustmentModal());
    },

    handlerAddProduct,
    handlerDeleteProduct,
  };
};
